import { useEffect, useState, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { AppBar, Box, CssBaseline, CSSObject, Drawer as MuiDrawer, IconButton, Toolbar, useMediaQuery, styled, useTheme } from '@mui/material';
import { Menu, MenuOpen } from '@mui/icons-material';
import Sidebar from '../Sidebar';
import { ErrorBoundary } from '@sentry/react';
import PageFallback from '../../components/fallbacks/PageFallback';
import appBarLogo from '../../assets/SurePass Logo Horz Black.svg';

const drawerWidth = 291;

// Transition Mixins

// Expands the sidebar to full width when open on desktop
const drawerOpenMixin = (theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  width: drawerWidth,
  overflowX: 'hidden',
});

// Contracts the sidebar to mini width when closed on desktop
const drawerClosedMixin = (theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: '64px',
});

// Pushes main content to the right when sidebar is open on desktop 
const mainContentDrawerOpenMixin = (theme): CSSObject => ({
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.easeOut,
    duration: theme.transitions.duration.enteringScreen
  }),
});

// Pulls main content to the left when sidebar is closed on desktop
const mainContentDrawerClosedMixin = (theme): CSSObject => ({
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
});

// Only used on desktop
const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'isSmallScreen'
})<{
  isSmallScreen?: boolean;
}>(({ theme, isSmallScreen }) => ({
  width: isSmallScreen ? 'auto' : drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  '& .MuiDrawer-paper': {
    backgroundColor: theme.palette.primary.contrastText,
  },
  variants: [
      {
        // Drawer open on desktop
        props: ({ open, isSmallScreen }) => open && !isSmallScreen,
        style: {
          ...drawerOpenMixin(theme),
          '& .MuiDrawer-paper': drawerOpenMixin(theme),
        },
      },
      {
        // Drawer closed on desktop
        props: ({ open, isSmallScreen }) => !open && !isSmallScreen,
        style: {
          ...drawerClosedMixin(theme),
          '& .MuiDrawer-paper': drawerClosedMixin(theme),
        },
      },
    ],
}));

const Main = styled(
  'main',
  { shouldForwardProp: (prop) => (
    prop !== 'open' &&
    prop !== 'gutterTop' &&
    prop !== 'gutterHorizontal' && 
    prop !== 'gutterBottom' &&
    prop !== 'isSmallScreen'
  )}
)<{
  open: boolean;
  gutterTop?: boolean;
  gutterHorizontal?: boolean;
  gutterBottom?: boolean;
  isSmallScreen: boolean;
}>(({
  theme,
  gutterTop = true, // should be false on pages that have breadcrumbs
  gutterHorizontal = true, // should be false on pages that have sections with background colors that need to be full width
  gutterBottom = true, // should be false on pages that fill the window and scroll their own content
  isSmallScreen,
}) => ({
  height: '100%',
  minWidth: 0,
  flexGrow: 1,
  paddingTop: (
    isSmallScreen
      ? gutterTop
        ? theme.spacing(11)
        : theme.spacing(10)
      : gutterTop
        ? theme.spacing(3)
        : theme.spacing(2)
  ),
  paddingBottom: gutterBottom ? theme.spacing(3) : 0,
  paddingLeft: gutterHorizontal ? theme.spacing(3) : 0,
  paddingRight: gutterHorizontal ? theme.spacing(3) : 0,
  variants: [{
    // Drawer open on desktop
    props: ({ open }) => open,
    style: mainContentDrawerOpenMixin(theme),
  }, {
    // Drawer closed on desktop
    props: ({ open }) => !open,
    style: mainContentDrawerClosedMixin(theme),
  }],
}));

const PageLayout = ({
  children,
  gutterHorizontal = true,
  gutterTop = true,
  gutterBottom = true,
}: {
  children: ReactNode;
  gutterHorizontal?: boolean;
  gutterTop?: boolean;
  gutterBottom?: boolean;
}) => {
  const theme = useTheme();
  const location = useLocation();
  const [open, setOpen] = useState<boolean>(true);
  const isSmallScreen = useMediaQuery('(max-width:1024px)');

  const toggleDrawer = () => {
    setOpen(!open);
  };

  useEffect(() => {
    setOpen(!isSmallScreen);
  }, [isSmallScreen]);

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <CssBaseline />
      {isSmallScreen && (
        <AppBar elevation={0} sx={{ backgroundColor: theme.palette.primary.contrastText }}>
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            <IconButton onClick={toggleDrawer} aria-label='Menu'>{open ? <MenuOpen /> : <Menu />}</IconButton>
            <Box component="img" src={appBarLogo} alt="SurePass logo" />
          </Toolbar>
        </AppBar>
      )}
      {isSmallScreen ? (
        <MuiDrawer
          variant="temporary"
          anchor="left"
          open={open}
          onClose={() => setOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              backgroundColor: theme => theme.palette.primary.contrastText,
            },
          }}
        >
          <Sidebar open={open} toggleDrawer={toggleDrawer} />
        </MuiDrawer>
      ) : (
        <Drawer variant="permanent" anchor="left" open={open}>
          <Sidebar open={open} toggleDrawer={toggleDrawer} />
        </Drawer>
      )}
      <Main
        open={open}
        gutterTop={gutterTop}
        gutterHorizontal={gutterHorizontal}
        gutterBottom={gutterBottom}
        isSmallScreen={isSmallScreen}
      >
        <ErrorBoundary fallback={<PageFallback />} key={location.pathname}>
          {children}
        </ErrorBoundary>
      </Main>
    </Box>
  );
};

export default PageLayout;
